import DefaultAvatar from '@/assets/images/default-avatar.jpg';
import DefaultCover from '@/assets/images/default-covers/FFFFFF.jpg';
import { Button } from '@/components/Button';
import { ColorPicker } from '@/components/ColorPicker';
import { CoverGallery } from '@/components/CoverGallery';
import { Image } from '@/components/Image';
import { ImageCropper } from '@/components/ImageCropper';
import { Loading } from '@/components/Loading';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { TextArea } from '@/components/TextArea';
import { TextInput } from '@/components/TextInput';
import { useCardStore } from '@/stores/useCardStore';
import { Card } from '@/types/card';
import { base64EncodeFile } from '@/utils/fileToBase64';
import getDefaultCover from '@/utils/getDefaultCover';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { BsCamera, BsUpload } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

type CardFormValues = Omit<
  Card,
  'company' | 'job' | 'full_name' | 'biography'
> & {
  company: string;
  job: string;
  full_name: string;
  biography: string;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

// Nombre total d'images de couverture disponibles
const TOTAL_COVER_IMAGES = 50;

export const UpdateCardInformations = () => {
  const { t } = useTranslation();
  const { card, updateCard, deleteCardImage, updateCardImage } = useCardStore();
  const [imageSrcForCrop, setImageSrcForCrop] = useState<string>('');
  const [imageCropperIsOpen, setImageCropperIsOpen] = useState<boolean>(false);
  const [currentImageType, setCurrentImageType] = useState<
    'avatar' | 'company_logo' | 'cover'
  >('avatar');
  const [coverModalIsOpen, setCoverModalIsOpen] = useState<boolean>(false);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [isUpdatingCover, setIsUpdatingCover] = useState(false);
  const navigate = useNavigate();
  const handleUpdateImage = (type: 'avatar' | 'company_logo' | 'cover') => {
    if (type === 'cover') {
      setCoverModalIsOpen(true);
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        setCurrentImageType(type);
        setImageSrcForCrop(URL.createObjectURL(file));
        setImageCropperIsOpen(true);
      }
    };
    input.click();
  };

  const handleUploadCoverImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        setCurrentImageType('cover');
        setImageSrcForCrop(URL.createObjectURL(file));
        setImageCropperIsOpen(true);
        setCoverModalIsOpen(false);
      }
    };
    input.click();
  };

  const handleSelectCoverImage = async (imageSrc: string) => {
    try {
      // Convertir l'image en File pour pouvoir l'utiliser avec le cropper
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' });

      setCurrentImageType('cover');
      setImageSrcForCrop(URL.createObjectURL(file));
      setImageCropperIsOpen(true);
      setCoverModalIsOpen(false);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDeleteImage = (type: 'avatar' | 'company_logo' | 'cover') => {
    try {
      if (!card) return;

      if (type === 'avatar') {
        setIsUpdatingAvatar(true);
      } else if (type === 'cover') {
        setIsUpdatingCover(true);
      }

      deleteCardImage(card.slug, type).finally(() => {
        if (type === 'avatar') {
          setIsUpdatingAvatar(false);
        } else if (type === 'cover') {
          setIsUpdatingCover(false);
        }
      });
    } catch (error) {
      handleError(error);
      if (type === 'avatar') {
        setIsUpdatingAvatar(false);
      } else if (type === 'cover') {
        setIsUpdatingCover(false);
      }
    }
  };

  const handleCropComplete = async (croppedImage: File) => {
    setImageCropperIsOpen(false);

    try {
      if (currentImageType === 'avatar') {
        setIsUpdatingAvatar(true);
      } else if (currentImageType === 'cover') {
        setIsUpdatingCover(true);
      }

      const base64Image = await base64EncodeFile(croppedImage);

      if (!card) return;
      await updateCardImage(card.slug, currentImageType, base64Image);
    } catch (error: unknown) {
      handleError(error);
    } finally {
      if (currentImageType === 'avatar') {
        setIsUpdatingAvatar(false);
      } else if (currentImageType === 'cover') {
        setIsUpdatingCover(false);
      }
    }
  };

  const handleSubmit = async (
    values: CardFormValues,
    { setErrors, setSubmitting }: FormikHelpers<CardFormValues>,
  ) => {
    try {
      setSubmitting(true);
      if (!card) return;

      await updateCard(card.slug, values);
      setSubmitting(false);
    } catch (error) {
      handleError(error, setErrors, navigate);
    } finally {
      setSubmitting(false);
    }
  };

  if (!card) return <Loading />;

  const initialValues: CardFormValues = {
    ...card,
    company: card.company || '',
    job: card.job || '',
    full_name: card.full_name || '',
    biography: card.biography || '',
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleChange, submitForm, isSubmitting }) => {
          return (
            <Form className="space-y-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="my-4 flex flex-col items-center gap-12 md:flex-row md:items-start md:gap-8"
              >
                {/* Profile Photo */}
                <motion.div
                  variants={itemVariants}
                  key="profile"
                  id="profile-picture-upload"
                  className="flex flex-col items-center md:items-start"
                >
                  <Text className="mb-2 text-xs font-medium text-gray-600">
                    {t('updateCard:images.profile')}
                  </Text>
                  <motion.div
                    className="group relative"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="relative overflow-hidden rounded-full">
                      <Image
                        refreshKey={card.updated_at}
                        className="size-24 min-w-24 rounded-full border-2 border-white object-cover shadow-sm"
                        src={card.avatar}
                        defaultImage={DefaultAvatar}
                        alt={t('updateCard:images.profile')}
                      />
                      {isUpdatingAvatar ? (
                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30">
                          <AiOutlineLoading3Quarters className="size-8 animate-spin text-white" />
                        </div>
                      ) : (
                        <div
                          onClick={() => handleUpdateImage('avatar')}
                          className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black/0 transition-colors duration-200 group-hover:bg-black/30"
                        >
                          <BsCamera className="size-8 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                        </div>
                      )}
                    </div>
                    <Text className="mt-2 text-center text-xs text-gray-300">
                      300 x 300 px
                    </Text>
                    {card?.avatar && !isUpdatingAvatar && (
                      <button
                        type="button"
                        onClick={() => handleDeleteImage('avatar')}
                        className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs font-medium text-danger transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100"
                      >
                        {t('updateCard:images.delete')}
                      </button>
                    )}
                  </motion.div>
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  key="cover"
                  id="cover-image-upload"
                  className="flex w-full max-w-[290px] flex-col items-center md:items-start"
                >
                  <Text className="mb-2 text-xs font-medium text-gray-600">
                    {t('updateCard:images.cover')}
                  </Text>
                  <motion.div
                    className="group relative w-full"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      className="relative flex h-24 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg"
                      onClick={() =>
                        !isUpdatingCover && handleUpdateImage('cover')
                      }
                    >
                      <Image
                        refreshKey={card.updated_at}
                        className=" size-full rounded-lg border-2 border-white object-cover shadow-sm"
                        src={card.cover}
                        defaultImage={
                          getDefaultCover(card.color) ?? DefaultCover
                        }
                        alt={t('updateCard:images.cover')}
                      />
                      {isUpdatingCover ? (
                        <div className="absolute inset-0 flex w-full items-center justify-center rounded-lg bg-black/30">
                          <AiOutlineLoading3Quarters className="size-8 animate-spin text-white" />
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex w-full items-center justify-center rounded-lg bg-black/0 transition-colors duration-200 group-hover:bg-black/30">
                          <BsCamera className="size-8 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                        </div>
                      )}
                    </div>
                    <Text className="mt-2 text-center text-xs text-gray-300">
                      391 x 220 px
                    </Text>
                    {card?.cover && !isUpdatingCover && (
                      <button
                        type="button"
                        onClick={() => handleDeleteImage('cover')}
                        className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs font-medium text-danger transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100"
                      >
                        {t('updateCard:images.delete')}
                      </button>
                    )}
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Modal pour sélectionner une image de couverture */}
              <Modal
                isOpen={coverModalIsOpen}
                onClose={() => setCoverModalIsOpen(false)}
                title={t('updateCard:images.selectCover')}
                className="max-w-2xl"
              >
                <div className="space-y-6">
                  {/* Option pour télécharger une image depuis l'ordinateur */}
                  <div
                    onClick={handleUploadCoverImage}
                    className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6 transition-colors duration-200 hover:border-gray-400 hover:bg-gray-100"
                  >
                    <BsUpload className="mb-2 size-8 text-gray-500" />
                    <Text className="text-center text-gray-500">
                      {t('updateCard:images.uploadFromComputer')}
                    </Text>
                  </div>

                  {/* Galerie d'images prédéfinies */}
                  <CoverGallery
                    onSelectImage={handleSelectCoverImage}
                    totalImages={TOTAL_COVER_IMAGES}
                  />
                </div>
              </Modal>

              <AnimatePresence>
                {imageCropperIsOpen && (
                  <ImageCropper
                    cropShape={currentImageType === 'cover' ? 'rect' : 'round'}
                    showGrid={false}
                    ratio={currentImageType === 'cover' ? 391 / 220 : 1}
                    imageSrc={imageSrcForCrop}
                    isOpen={imageCropperIsOpen}
                    onClose={() => setImageCropperIsOpen(false)}
                    onCropIsComplete={handleCropComplete}
                  />
                )}
              </AnimatePresence>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div
                  variants={itemVariants}
                  className="flex items-start"
                >
                  <TextInput
                    containerStyle="w-full"
                    type="text"
                    name="full_name"
                    label={t('updateCard:informations.fullName')}
                    placeholder={t('updateCard:informations.fullName')}
                    id="edit-card-name"
                  />
                </motion.div>

                <motion.div
                  variants={itemVariants}
                  className="mt-4 flex items-start gap-4"
                >
                  <TextInput
                    containerStyle="flex-1"
                    type="text"
                    name="company"
                    label={t('updateCard:informations.company')}
                    placeholder={t('updateCard:informations.company')}
                  />
                  <TextInput
                    containerStyle="flex-1"
                    type="text"
                    name="job"
                    label={t('updateCard:informations.job')}
                    placeholder={t('updateCard:informations.job')}
                  />
                </motion.div>

                <motion.div
                  variants={itemVariants}
                  className="mt-4 flex flex-col space-y-2"
                >
                  <TextArea
                    name="biography"
                    label={t('updateCard:informations.biography')}
                    placeholder={t('updateCard:informations.biography')}
                    maxLength={250}
                  />

                  <motion.div variants={itemVariants}>
                    <Text className="mb-2 mt-4 text-xs font-medium text-gray-600">
                      {t('updateCard:design')}
                    </Text>

                    <div className="flex items-start">
                      <Text className="mr-4 w-40 text-sm text-linko-dark">
                        {t('updateCard:cardColor')}:
                      </Text>
                      <ColorPicker
                        name="color"
                        selectedColor={card.color}
                        onChange={e => {
                          handleChange(e);
                          submitForm();
                        }}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                className="mt-8"
              >
                <Button
                  disabled={isSubmitting}
                  type="submit"
                  className="w-full"
                >
                  {t('common:save')}
                </Button>
              </motion.div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
